import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchBusinessInfo,
  fetchServices,
  fetchStaff,
  ApiError,
  type BusinessInfo,
  type ServiceInfo,
  type StaffInfo,
} from "../lib/api.ts";
import { EmptyState } from "./EmptyState.tsx";
import { ClassicFlow } from "../flows/ClassicFlow.tsx";
import { ProviderFlow } from "../flows/ProviderFlow.tsx";
import { DateFirstFlow } from "../flows/DateFirstFlow.tsx";
import { SimpleFlow } from "../flows/SimpleFlow.tsx";
import { CatalogFlow } from "../flows/CatalogFlow.tsx";

// ─── Branding helpers ────────────────────────────────────────────────────────

const RADIUS_MAP: Record<string, string> = {
  sharp: "4px",
  rounded: "12px",
  pill: "20px",
};

const FONT_MAP: Record<string, string> = {
  inter: "'Inter', sans-serif",
  poppins: "'Poppins', sans-serif",
  lato: "'Lato', sans-serif",
  roboto: "'Roboto', sans-serif",
  montserrat: "'Montserrat', sans-serif",
  playfair: "'Playfair Display', serif",
  raleway: "'Raleway', sans-serif",
};

const GOOGLE_FONT_FAMILIES: Record<string, string> = {
  poppins: "Poppins:wght@400;500;600;700",
  lato: "Lato:wght@400;700",
  roboto: "Roboto:wght@400;500;700",
  montserrat: "Montserrat:wght@400;500;600;700",
  playfair: "Playfair+Display:wght@400;600;700",
  raleway: "Raleway:wght@400;500;600;700",
};

function applyBranding(info: BusinessInfo) {
  const root = document.documentElement;

  if (info.primary_color) {
    root.style.setProperty("--accent", info.primary_color);
  }

  const radiusKey = (info.corner_radius ?? "rounded").toLowerCase();
  const radius = RADIUS_MAP[radiusKey] ?? "12px";
  root.style.setProperty("--radius", radius);

  const fontKey = (info.font_family ?? "inter").toLowerCase();
  const font = FONT_MAP[fontKey] ?? "'Inter', sans-serif";
  root.style.setProperty("--font", font);

  // Load Google Font for non-Inter fonts
  if (fontKey !== "inter" && GOOGLE_FONT_FAMILIES[fontKey]) {
    const linkId = "booking-google-font";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONT_FAMILIES[fontKey]}&display=swap`;
      document.head.appendChild(link);
    }
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BookingShell() {
  const { slug } = useParams<{ slug: string }>();

  const [business, setBusiness] = useState<BusinessInfo | null>(null);
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [staff, setStaff] = useState<StaffInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    Promise.all([fetchBusinessInfo(slug), fetchServices(slug), fetchStaff(slug)])
      .then(([biz, svcs, stf]) => {
        setBusiness(biz);
        setServices(svcs.filter((s) => s.is_active !== false));
        setStaff(stf.filter((s) => s.is_active));
        applyBranding(biz);
        document.title = `Book at ${biz.name} | Zedule`;
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setError("404");
        } else {
          setError("Failed to load booking page. Please try again.");
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="booking-shell min-h-screen flex items-center justify-center">
        <div className="text-gray-500 text-sm animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error === "404") {
    return (
      <div className="booking-shell">
        <EmptyState
          icon="🔍"
          title="Page not found"
          description="The booking page you're looking for doesn't exist."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-shell min-h-screen flex items-center justify-center px-4">
        <p className="text-red-600 text-center">{error}</p>
      </div>
    );
  }

  if (!business) return null;

  // No active services → empty state with phone CTA
  if (services.length === 0) {
    return (
      <div className="booking-shell">
        <EmptyState
          icon="📅"
          title="No services available"
          description="This business is not accepting online bookings at the moment."
          phone={business.phone ?? undefined}
          whatsapp={business.whatsapp ?? undefined}
        />
      </div>
    );
  }

  const flowProps = { business, services, staff, slug: slug! };

  const template = business.booking_template ?? "classic";

  return (
    <div className="booking-shell">
      {template === "provider" && <ProviderFlow {...flowProps} />}
      {template === "datefirst" && <DateFirstFlow {...flowProps} />}
      {template === "simple" && <SimpleFlow {...flowProps} />}
      {template === "catalog" && <CatalogFlow {...flowProps} />}
      {(template === "classic" || !["provider", "datefirst", "simple", "catalog"].includes(template)) && (
        <ClassicFlow {...flowProps} />
      )}
    </div>
  );
}

export default BookingShell;
